"use client";

import { GetEventFromSlug } from "../actions";
import { EventManageActions } from "./EventActions";
import { cn } from "~/utils/fontsConfig";
import { LinkWrapper } from "~/components/v4/shadcn/LinkWrapper";
import { CommunityAvatar } from "~/components/v4/shadcn/CommunityAvatar";
import { slugifyUser } from "~/utils/string";
import { useEventContextV2, useWhiteLabel } from "~/app/providers";
import { env } from "~/env.mjs";
import { UserAvatar } from "~/components/v4/shadcn/UserAvatar";
import { getEventStatusColor } from "../utils/colors";
import { TabsWrapper } from "./TabsWrapper";

import { useState } from "react";

import { IoShareSocialOutline } from "react-icons/io5";
import ShareEventModal from "~/app/events/[slug]/share/_components/ShareEventModal";
import { CustomizableLayout } from "~/types/CustomizableLayout";
import { EventJourneyStatus } from "@prisma/client";

const getStatusText = (status: EventJourneyStatus) => {
    if (!status) return "";

    if (status === EventJourneyStatus.DRAFT_PROPOSED) {
        return "Draft";
    }

    return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
};

export const EventNameHeader = ({
    event,
    variant = "full",
    className,
    isManagePage,
    customization,
}: {
    event: GetEventFromSlug;
    variant?: "full" | "condensed";
    className?: string;
    isManagePage?: boolean;
    customization?: CustomizableLayout;
}) => {
    const [shareModalOpen, setShareModalOpen] = useState(false);

    const hostProfile =
        event?.community !== null
            ? {
                  name: event?.community?.name ?? "",
                  image: `${env.NEXT_PUBLIC_ASSETS_URL}/community/${event?.community?.id}/${event?.community?.image}`,
                  href: `/beta/${event.community?.slug}`,
              }
            : {
                  name: event?.user?.name ?? "no name",
                  image: event?.user?.image ?? "",
                  href: event?.user
                      ? `/profile/${slugifyUser(event.user)}`
                      : "",
              };

    return (
        <div
            className={cn(
                variant === "condensed" && "flex items-center gap-4",
                variant === "full" && "space-y-3",
            )}
        >
            <LinkWrapper href={hostProfile.href} className="shrink-0">
                <div className="flex items-center gap-2">
                    {event.community ? (
                        <CommunityAvatar
                            community={event.community}
                            width={32}
                            height={32}
                            className="h-11 w-11"
                        />
                    ) : (
                        <UserAvatar
                            user={event.user || hostProfile}
                            width={32}
                            height={32}
                            size={10}
                            className="h-11 w-11"
                        />
                    )}
                    <h1
                        className={cn(
                            "text-base font-medium",
                            variant === "condensed" && "hidden",
                        )}
                    >
                        {hostProfile.name}
                    </h1>
                </div>
            </LinkWrapper>

            <div className="flex h-full w-full items-center gap-2">
                <div className="grid w-full grid-cols-[1fr,auto] items-center gap-2">
                    <div className="flex items-center gap-2">
                        <LinkWrapper href={`/beta/events/${event.slug}`}>
                            <h1
                                className={cn("text-lg md:text-2xl", className)}
                            >
                                {event?.name}
                            </h1>
                        </LinkWrapper>
                        <span
                            className={cn(
                                "hidden rounded-lg p-1 px-2 text-sm text-white",
                                getEventStatusColor(event.eventJourneyStatus),
                                isManagePage && "md:block",
                            )}
                        >
                            {getStatusText(event.eventJourneyStatus)}
                        </span>
                    </div>

                    <div className="flex h-full flex-col items-center justify-between gap-2">
                        <button
                            onClick={() => setShareModalOpen(true)}
                            className="ml-auto"
                        >
                            <IoShareSocialOutline className="h-7 w-7 text-v3-N-300" />
                        </button>
                        <span
                            className={cn(
                                "rounded-lg p-1 px-2 text-sm",
                                getEventStatusColor(event.eventJourneyStatus),
                                "md:hidden",
                            )}
                        >
                            {event?.eventJourneyStatus
                                ? event.eventJourneyStatus
                                      .charAt(0)
                                      .toUpperCase() +
                                  event.eventJourneyStatus
                                      .slice(1)
                                      .toLowerCase()
                                : ""}
                        </span>
                    </div>
                </div>
            </div>

            <ShareEventModal
                isVisible={shareModalOpen}
                setIsVisible={setShareModalOpen}
                event={event}
                customization={customization}
            />
        </div>
    );
};

export const EventManagementHeader = () => {
    const event = useEventContextV2();
    const customization = useWhiteLabel()?.customization;
    return (
        <div
            className={cn(
                "flex flex-col gap-6 rounded-md bg-v3-N-600 !pb-0 pt-4",
                customization?.mainCardClassName,
            )}
        >
            <div className="flex flex-col justify-between gap-4 px-4 xs:gap-2 sm:flex-row sm:items-center sm:px-6">
                <h6
                    className={cn(
                        "w-full sm:w-auto",
                        customization?.labelClassName,
                    )}
                >
                    Event management
                </h6>
                <EventManageActions
                    event={event}
                    customization={customization}
                />
            </div>

            <div className="space-y-4 px-4 sm:px-6">
                <EventNameHeader
                    event={event}
                    isManagePage
                    customization={customization}
                />
            </div>

            <TabsWrapper eventSlug={event?.slug ?? ""} />
        </div>
    );
};
